/**
 * SlotsTable - Tabela de horários vagos agrupada por dia (mockup v2).
 * Cada dia é colapsável, cada linha mostra: horário, profissional, procedimento, sala, compatíveis.
 */
import React, { useState, useMemo } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import styles from '../WaitingListV2Page.module.css';

// Helper: format date to "Terça-feira, 10 de junho"
function formatDayTitle(dateStr) {
  const date = new Date(dateStr + 'T12:00:00');
  const weekdays = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
  const months = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
  return `${weekdays[date.getDay()]}, ${date.getDate()} de ${months[date.getMonth()]}`;
}

// Get initials from resource name
function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').filter(w => w.length > 2).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function SlotsTable({ slots = [], onViewPatients }) {
  // First day always open, rest collapsed
  const [expandedDays, setExpandedDays] = useState(() => {
    if (slots.length === 0) return {};
    const firstDay = slots[0]?.date;
    return firstDay ? { [firstDay]: true } : {};
  });

  // Group slots by date
  const groupedSlots = useMemo(() => {
    const groups = {};
    slots.forEach((slot) => {
      const dateKey = slot.date;
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(slot);
    });
    // Sort by date
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [slots]);

  const toggleDay = (dateKey) => {
    setExpandedDays((prev) => ({ ...prev, [dateKey]: !prev[dateKey] }));
  };

  if (slots.length === 0) {
    return (
      <div className={styles.emptyState}>
        <EventBusyIcon className={styles.emptyIcon} />
        <div className={styles.emptyTitle}>Nenhum horário vago encontrado</div>
        <div className={styles.emptySubtitle}>
          Ajuste o período ou os filtros para encontrar horários disponíveis.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.slotsTableContainer}>
      {groupedSlots.map(([dateKey, daySlots]) => {
        const isExpanded = expandedDays[dateKey];
        return (
          <div key={dateKey} className={styles.dayGroup}>
            {/* Day header */}
            <div className={styles.dayHeader} onClick={() => toggleDay(dateKey)}>
              <span className={styles.dayTitle}>{formatDayTitle(dateKey)}</span>
              <span className={styles.dayCount}>
                {daySlots.length} horário{daySlots.length !== 1 ? 's' : ''}
                <ExpandMoreIcon
                  className={`${styles.dayChevron} ${isExpanded ? styles.dayChevronOpen : ''}`}
                />
              </span>
            </div>

            {/* Slot rows */}
            {isExpanded && (
              <>
                <div className={styles.slotTableHeader}>
                  <span className={styles.slotTableHeaderCell}>Horário</span>
                  <span className={styles.slotTableHeaderCell}>Profissional</span>
                  <span className={styles.slotTableHeaderCell}>Procedimento</span>
                  <span className={styles.slotTableHeaderCell}>Sala</span>
                  <span className={styles.slotTableHeaderCell}>Compatíveis</span>
                </div>
                {daySlots.map((slot, idx) => (
                  <div key={`${dateKey}-${idx}`} className={styles.slotRow}>
                    {/* Time */}
                    <div className={styles.slotTime}>
                      <span className={styles.slotTimeValue}>{slot.time}</span>
                      <span className={styles.slotDuration}>{slot.duration || 30} min</span>
                    </div>

                    {/* Resource/Professional */}
                     <div className={styles.slotResource}>
                       {slot.resourceName ? (
                         <>
                           <div className={styles.slotResourceAvatar}>
                             {getInitials(slot.resourceName)}
                           </div>
                           <div className={styles.slotResourceInfo}>
                             <span className={styles.slotResourceName}>
                               {slot.resourceName}
                             </span>
                             {slot.resourceSpecialty && (
                               <span className={styles.slotResourceSpecialty}>
                                 {slot.resourceSpecialty}
                               </span>
                             )}
                           </div>
                         </>
                       ) : (
                         <span style={{ fontSize: '0.82rem', color: '#94a3b8', fontStyle: 'italic' }}>
                           Qualquer profissional
                         </span>
                       )}
                     </div>

                    {/* Procedure */}
                    <span className={styles.slotProcedure}>
                      {slot.procedureName || '—'}
                    </span>

                    {/* Room */}
                    <span className={styles.slotRoom}>
                      {slot.roomName || '—'}
                    </span>

                    {/* Compatible patients */}
                    <div className={styles.slotCompatible}>
                      {(slot.compatibleCount || 0) > 0 ? (
                        <>
                          <span className={styles.compatibleChip}>
                            <span className={styles.compatibleDot} />
                            {slot.compatibleCount} paciente{slot.compatibleCount !== 1 ? 's' : ''}
                          </span>
                          <Tooltip title="Ver oportunidades">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewPatients?.(slot);
                              }}
                              sx={{
                                background: 'var(--color-blue-main, #5A80FB)',
                                color: '#fff',
                                borderRadius: '8px',
                                padding: '6px',
                                '&:hover': { background: 'var(--color-main-dark, #26347B)' },
                              }}
                            >
                              <VisibilityIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <span style={{ fontSize: '0.78rem', color: '#cbd5e1' }}>—</span>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default SlotsTable;
